import React, { useState, useEffect, createContext } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AdminRoutes } from './AppRoutes';
import AdminLayout from './components/layout/AdminLayout/AdminLayout';
import { getAuth } from 'firebase/auth';
import { GetAllItems, GetAllItemsP } from './utils/functions';
import { ToastContainer } from 'react-toastify';
import './custom.css';
import 'react-toastify/dist/ReactToastify.css';
import { useMatch } from "react-router-dom";
import { Home } from "./pages/Home";
import Contact from "./pages/Contact";
import Login from "./pages/authentication/login/index";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";
import TemplatePropertyDetail from "./components/templates/TemplatePropertyDetail";
import PropertyCollection from "./pages/PropertyCollection";
import ResetPassword from "./pages/authentication/login/indexReset";
import TemplateExperienceSlider from "./components/templates/TemplateExperienceSlider";
import TemplateExperienceDetail from "./components/templates/TemplateExperienceDetail";
import TemplateBox from './components/templates/TemplateBox';
import WebLayout from './components/layout/WebLayout/WebLayout';
import LoadingApp from "./components/loadingApp/loadingApp";
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider } from 'react-helmet-async';
import PropertyRules from './pages/PropertyRules';

export const DataContext = createContext({
  user: null,
  users: null,
  properties: null,
  experiences: null,
  categories: null,
  pages: null,
  updateData: null,
  loading: null
});

export const App = () => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [properties, setProperties] = useState(null);
  const [experiences, setExperiences] = useState(null);
  const [categories, setCategories] = useState(null);
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [weddings, setWeddings] = useState(null);
  const auth = getAuth();
  const locationAdmin = useMatch("admin/*") ? true : false;

  const propertyRoutes = properties ? properties.filter(x => x.status === "active").map((item) => {
      const pathName = item.seoInformation ? item.seoInformation.linkUrl.toLowerCase().replace(/\s+/g, '-') : item.name.toLowerCase().replace(/\s+/g, '-');
      return {
        index: false,
        element: <TemplatePropertyDetail villa={item} />,
        name: item.name,
        path: `/property-collection/${pathName}`
      };
  }) : [];

  const experiencesCategory = pages ? pages.find(item => item.name === 'experiences').pageContent.experiences.map((item) => {
      return {
        index: false,
        element: <TemplateExperienceSlider item={item} />,
        name: item.title,
        path: `/experiences/${item.cta?.url}`
      };

  }) : [];

  const experiencesRoutes = experiences ? experiences.filter(x => x.status === "active").map((item) => {
      return {
        index: false,
        element: <TemplateExperienceDetail experience={item} />,
        name: item.title,
        path: `/experiences/${item.category}/${item.seoInformation.linkUrl}`,
      }
  }) : [];

  const AppRoutes = [
    /* Home page & general page */
    { index: true, element: <Home />, name: "Home Page", path: "/" },
    { index: false, element: <PropertyCollection />, name: "Property Collection", path: "/property-collection" },
    { index: false, element: <TemplateBox />, name: "Experiences", path: "/experiences" },
    { index: false, element: <TemplateExperienceDetail experience={weddings} />, name: "Weddings", path: "/weddings" },
    { index: false, element: <Contact />, name: "Contact us", path: "/contact-us" },
    { index: false, element: <PropertyRules />, name: "Property Rules", path: "property-rules" },
    { index: false, element: <TermsConditions />, name: "Terms & Conditions", path: "/terms-conditions" },
    { index: false, element: <PrivacyPolicy />, name: "Privacy Policy", path: "/privacy-policy" },
    ...propertyRoutes,
    ...experiencesCategory,
    ...experiencesRoutes,
    { index: false, element: <Login />, name: "Login", path: "/admin/login" },
    { index: false, element: <ResetPassword />, name: "Reset Password", path: "/admin/reset-password" },
    { index: false, element: <NotFound />, name: "Dashboard", path: "*" },
  ];

  useEffect(() => {
    if (!pages) return; 
    setWeddings(pages.find(x => x.name === "weddings")?.pageContent);
  }, [pages]);

  useEffect(() => {
    const initializeData = async (currentUser) => {
      try {
        setLoading(true);
        if (currentUser) {
          if (locationAdmin) {
            const fetchedUsers = await GetAllItemsP("User");
            setUsers(fetchedUsers);
          }
        } else {
          setUsers([]);
        }

        const [pagesData, propertiesData, experiencesData, categoriesData] = await Promise.all([
          !locationAdmin ? GetAllItems("Page", false) : null,
          GetAllItems("Property", false),
          GetAllItems("Experiences", false),
          GetAllItems("Categories", false)
        ]);

        setPages(pagesData);
        setProperties(propertiesData);
        setExperiences(experiencesData);
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      initializeData(currentUser);
    });
  
    return () => unsubscribe();
    // eslint-disable-next-line
  }, [auth, locationAdmin]);

  const onChangeUpdate = async (key) => {
    switch (key) {
      case "users":
        setUsers(await GetAllItemsP("User"));
        break;
      case "properties-collection":
        setProperties(await GetAllItems("Property", true));
        break;
      case "pages":
        setPages(await GetAllItems("Page", true));
        break;
      case "experiences":
        setExperiences(await GetAllItems("Experiences", true));
        break;
      case "categories":
        setCategories(await GetAllItems("Categories", true));
        break;
      default: break;
    }
  };

  return (
    <>
      {(!loading && auth.currentUser && locationAdmin) ? (
        <AdminLayout>
          <DataContext.Provider value={{
            user: user,
            users: users,
            properties: properties,
            experiences: experiences,
            categories: categories,
            pages: pages,
            loading: loading,
            updateData: onChangeUpdate
          }}>
            <Routes>
              {AdminRoutes.map((route, index) => {
                const { element, ...rest } = route;
                return <Route key={index} {...rest} element={element} />;
              })}
            </Routes>
          </DataContext.Provider>
        </AdminLayout>
      ) 
      : (!loading) ? (
        <WebLayout>
        <DataContext.Provider value={{
          properties: properties,
          experiences: experiences,
          categories: categories,
          pages: pages,
          loading: loading
        }}>
          <ScrollToTop/>
          <HelmetProvider>
          <Routes>
            {AppRoutes.map((route, index) => {
              const { element, ...rest } = route;
              return <Route key={index} {...rest} element={element} />;
            })}
          </Routes>
          </HelmetProvider>
        </DataContext.Provider>
        </WebLayout>
      )
      :
      <LoadingApp />
    }

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover={true}
      />
    </>
  );
};
